import { type PropsWithChildren, useEffect, useState, type FC } from "react";
import { IntlProvider as ReactIntlProvider } from "react-intl";
import type { Locale } from "@project/common";
import { useLocale } from "../hooks/locale";

const loadMessages = async (locale: Exclude<Locale, "en">): Promise<Partial<Record<Locale, Record<string, string>> | null>> => {
  const {
    default: messages,
  } = await import(`../../lang/${locale}.json`) as { default: Record<string, { defaultMessage: string }> };

  return { [locale]: Object.entries(messages).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = value.defaultMessage;
    return acc;
  }, {}),
  };
};

type Messages = Partial<Record<Locale, Record<string, string>>>;

const defaultMessages: Messages = { en: {} };

export const IntlProvider: FC<PropsWithChildren> = ({ children }) => {
  const locale = useLocale();
  const [messages, setMessages] = useState(defaultMessages);

  useEffect(() => {
    if (locale === "en") {
      return;
    }
    if (!messages[locale]) {
      void loadMessages(locale).then((data) => {
        if (data) {
          setMessages(prev => ({
            ...prev,
            ...data,
          }));
        }
      });
    }
  }, [locale, messages]);

  if (messages[locale]) {
    return (
      <ReactIntlProvider
        key={locale}
        locale={locale}
        messages={messages[locale]}
      >
        {children}
      </ReactIntlProvider>
    );
  }
  else {
    return (
      <ReactIntlProvider
        key="en"
        defaultLocale="en"
        locale="en"
        messages={defaultMessages.en}
      >
        {children}
      </ReactIntlProvider>
    );
  }
};
